import { AiPrediction, Linkrole } from 'app/api/tag-queries/ai-tagging';
import { PresentationTreeResponse, TagOrigin } from 'app/api/tag-queries/types';

export type ResultTab = 'rad' | 'koncept' | 'lista';

export const cellKey = (row: number, col: number) => `${row}:${col}`;
export const rowKey = (row: number, col: number, conceptId: string, linkroleUri: string) => `${row}:${col}:${conceptId}:${linkroleUri}`;

export interface PredRow {
  kind: 'pred';
  key: string;
  row: number;
  col: number;
  value: string;
  text: string;
  p: AiPrediction;
  lr: Linkrole;
  rows: Linkrole[];
  applied: boolean;
  origin: TagOrigin | null;
}

export interface EmptyRow {
  kind: 'empty';
  key: string;
  value: string;
  text: string;
  muted?: boolean;
}

// Injected above an expanded group's children — the second-level (leaf) column header.
export interface LeafHeaderRow {
  kind: 'leaf-header';
  key: string;
}

export interface CellGroupRow {
  kind: 'cell-group';
  key: string; // cellKey(row, col)
  row: number;
  col: number;
  value: string;
  text: string; // combined "row — column" (aria/title)
  rowText: string; // row header alone
  colText: string; // column header alone
  top: PredRow | null;
  candidateCount: number;
  appliedCount: number;
  expanded: boolean;
}

export interface ConceptGroupRow {
  kind: 'concept-group';
  key: string; // `${conceptId}:${linkroleUri}`
  conceptId: string;
  conceptName: string;
  descLabel: string;
  lr: Linkrole;
  cellCount: number;
  appliedCount: number;
  f1: number;
  members: PredRow[];
  expanded: boolean;
}

export type GroupedRow = PredRow | EmptyRow | LeafHeaderRow | CellGroupRow | ConceptGroupRow;

export interface CellGroup {
  row: number;
  col: number;
  cellKey: string;
  value: string;
  text: string;
  rowText: string;
  colText: string;
  preds: PredRow[];
}

export interface ConceptOrderIndex {
  linkroleOrder: Map<string, number>; // roleURI -> position in networks[]
  conceptOrder: Map<string, Map<string, number>>; // roleURI -> (conceptName/qname == node.id -> depth-first position)
}

export function buildConceptOrderIndex(tree: PresentationTreeResponse | null | undefined): ConceptOrderIndex {
  const linkroleOrder = new Map<string, number>();
  const conceptOrder = new Map<string, Map<string, number>>();
  const networks = tree?.presentationTree?.networks ?? [];

  networks.forEach((net, i) => {
    const uri = net.linkrole?.roleURI;
    if (!uri) return;
    if (!linkroleOrder.has(uri)) linkroleOrder.set(uri, i);
    const positions = conceptOrder.get(uri) ?? new Map<string, number>();
    let pos = positions.size;
    const walk = (nodes: { id: string; order?: number; children?: any[] }[]) => {
      [...nodes]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .forEach((node) => {
          if (node.id != null && !positions.has(node.id)) positions.set(node.id, pos++);
          if (node.children?.length) walk(node.children);
        });
    };
    walk(net.roots ?? []);
    conceptOrder.set(uri, positions);
  });

  return { linkroleOrder, conceptOrder };
}

export function topPred(cell: CellGroup): PredRow | null {
  const selectable = cell.preds.filter((p) => !p.applied);
  if (!selectable.length) return null;
  return selectable.reduce((best, p) => (p.p.f1ScoreTest > best.p.f1ScoreTest ? p : best));
}

const byF1Desc = (a: PredRow, b: PredRow) => b.p.f1ScoreTest - a.p.f1ScoreTest;
const distinctConcepts = (preds: PredRow[]) => new Set(preds.map((p) => p.p.conceptId)).size;

export function flattenLista(cells: CellGroup[]): GroupedRow[] {
  const out: GroupedRow[] = [];
  for (const c of cells) {
    if (!c.preds.length) out.push({ kind: 'empty', key: c.cellKey, value: c.value, text: c.text });
    else out.push(...c.preds);
  }
  return out;
}

export function flattenRad(cells: CellGroup[], expanded: Set<string>): GroupedRow[] {
  const out: GroupedRow[] = [];
  for (const c of cells) {
    if (!c.preds.length) {
      out.push({ kind: 'empty', key: c.cellKey, value: c.value, text: c.text, muted: true });
      continue;
    }
    const isExpanded = expanded.has(c.cellKey);
    out.push({
      kind: 'cell-group',
      key: c.cellKey,
      row: c.row,
      col: c.col,
      value: c.value,
      text: c.text,
      rowText: c.rowText,
      colText: c.colText,
      top: topPred(c),
      candidateCount: distinctConcepts(c.preds),
      appliedCount: c.preds.filter((p) => p.applied).length,
      expanded: isExpanded,
    });
    if (isExpanded) {
      out.push({ kind: 'leaf-header', key: `${c.cellKey}:__head` });
      out.push(...[...c.preds].sort(byF1Desc));
    }
  }
  return out;
}

export function flattenKoncept(cells: CellGroup[], order: ConceptOrderIndex, expanded: Set<string>): GroupedRow[] {
  interface Group {
    key: string;
    conceptId: string;
    conceptName: string;
    descLabel: string;
    lr: Linkrole;
    members: PredRow[];
  }
  const groups = new Map<string, Group>();
  for (const c of cells) {
    for (const p of c.preds) {
      const key = `${p.p.conceptId}:${p.lr.uri}`;
      let g = groups.get(key);
      if (!g) {
        g = { key, conceptId: p.p.conceptId, conceptName: p.p.conceptName, descLabel: p.p.label || p.p.conceptName, lr: p.lr, members: [] };
        groups.set(key, g);
      }
      g.members.push(p);
    }
  }

  const rankOf = (g: Group): [number, number] => {
    const lrRank = order.linkroleOrder.get(g.lr.uri);
    if (lrRank === undefined) return [Infinity, Infinity];
    const conceptRank = order.conceptOrder.get(g.lr.uri)?.get(g.conceptName);
    return [lrRank, conceptRank ?? Infinity];
  };
  const maxF1 = (g: Group) => g.members.reduce((m: number, p: PredRow) => Math.max(m, p.p.f1ScoreTest), 0);

  const sorted = Array.from(groups.values()).sort((a, b) => {
    const [la, ca] = rankOf(a);
    const [lb, cb] = rankOf(b);
    if (la !== lb) return la - lb;
    if (ca !== cb) return ca - cb;
    return maxF1(b) - maxF1(a); // unmatched (or same-rank) ties → highest F1 first
  });

  const out: GroupedRow[] = [];
  for (const g of sorted) {
    const isExpanded = expanded.has(g.key);
    out.push({
      kind: 'concept-group',
      key: g.key,
      conceptId: g.conceptId,
      conceptName: g.conceptName,
      descLabel: g.descLabel,
      lr: g.lr,
      cellCount: g.members.length,
      appliedCount: g.members.filter((p) => p.applied).length,
      f1: maxF1(g),
      members: g.members,
      expanded: isExpanded,
    });
    if (isExpanded) {
      out.push({ kind: 'leaf-header', key: `${g.key}:__head` });
      out.push(...g.members);
    }
  }
  return out;
}

export function cellTopPredictionKey(cell: CellGroup): string | null {
  return topPred(cell)?.key ?? null;
}

export function conceptGroupEligibleKeys(members: PredRow[], checked: Set<string>): string[] {
  return members
    .filter((m) => {
      if (m.applied) return false;
      const siblingChecked = m.rows.some((other) => other.uri !== m.lr.uri && checked.has(rowKey(m.row, m.col, m.p.conceptId, other.uri)));
      return !siblingChecked;
    })
    .map((m) => m.key);
}

export function groupCheckState(keys: string[], checked: Set<string>): 'none' | 'some' | 'all' {
  if (!keys.length) return 'none';
  const n = keys.filter((k) => checked.has(k)).length;
  if (n === 0) return 'none';
  return n === keys.length ? 'all' : 'some';
}
